import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import { useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router';

import useFormInitialSet from '@/hooks/useFormInitialSet';
import { useSelectScrollSearch } from '@/hooks/useSelectScrollSearch';
import { useSelectPropsWithInitial } from '@/hooks/useSelectWithInitialValue';

const useTicketListFilter = (formProps: FormProps, filters: any[]) => {
  const { form } = formProps;
  const companyId = Form.useWatch('empresa', formProps.form);
  const initialValues: Record<string, any> = useMemo(
    () =>
      Object.fromEntries(
        filters.map(filter => {
          const valueParsed = parseInt(filter.value, 10);
          const value = isNaN(valueParsed) ? filter.value : valueParsed;

          return [filter.field, value];
        }),
      ),
    [filters],
  );
  const { setInitialValuesSetted } = useFormInitialSet(form, initialValues, [
    {
      watchField: 'empresa',
      resetFields: ['movil'],
    },
  ]);

  useEffect(() => {
    setInitialValuesSetted(false);
  }, [initialValues, setInitialValuesSetted]);

  const { usersSelectProps, companySelectProps, subStatusSelectProps, subCategorySelectProps } =
    useOutletContext<{
      usersSelectProps: any;
      companySelectProps: any;
      subStatusSelectProps: any;
      subCategorySelectProps: any;
    }>();
  // Custom hook para utilizar scroll infinito en selector de maquinas. Debe ser llamado en este punto para poder utilizar CompanyId en base al useWatch
  const { selectProps: machineSelectProps } = useSelectScrollSearch({
    resource: 'moviles',
    optionLabel: (item: any) => item.description,
    optionValue: (item: any) => item.id,
    filters: [
      {
        value: companyId,
        operator: 'eq' as const,
        field: 'empresa',
      },
    ],

    meta: {
      paginate: 1,
      full: 0,
      showNoUnit: true,
      showDisabled: true,
    },
  });
  const { selectProps: countrySelectProps } = useSelectScrollSearch({
    resource: 'paises',
    optionLabel: (item: any) => item.nombre,
    optionValue: (item: any) => item.id,
  });
  const companySelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: companySelectProps, // viene del contexto/superior
    resource: 'empresas',
    initialValue: initialValues.empresa,
    labelKey: 'full_name',
    valueKey: 'id',
  });
  const machineSelectPropsWithInitials = useSelectPropsWithInitial({
    selectProps: machineSelectProps,
    resource: 'moviles',
    initialValue: initialValues.movil,
    labelKey: 'description',
    valueKey: 'id',
  });

  useEffect(() => {
    if (filters.some(filter => filter.field === 'assigned_to_str')) {
      const assignedToFilter = filters.find(filter => filter.field === 'assigned_to_str').value;
      const userInOptions = usersSelectProps.options.find(
        option => option.item.username === assignedToFilter,
      );

      if (userInOptions) {
        form?.setFieldValue('assigned_to', userInOptions.value);
      }
    }
  }, [filters, form, usersSelectProps.options]);

  return {
    usersSelectProps,
    companySelectProps: companySelectPropsWithInitials,
    subStatusSelectProps,
    subCategorySelectProps,
    countrySelectProps,
    machineSelectProps: machineSelectPropsWithInitials,
  };
};

export default useTicketListFilter;
